import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';

import { cn } from '../../lib/utils';
import { buttonVariants } from './button';

export type CalendarProps = DayPickerProps;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        root: 'relative w-fit',
        months: 'flex flex-col gap-4',
        month: 'space-y-4',
        month_caption: 'flex h-9 w-full items-center justify-center px-10',

        caption_label: 'text-center text-sm font-semibold leading-none',

        nav: 'absolute left-3 right-3 top-3 flex h-9 items-center justify-between',

        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto h-9 w-9 rounded-md border bg-background p-0 hover:bg-accent'
        ),

        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto h-9 w-9 rounded-md border bg-background p-0 hover:bg-accent'
        ),

        month_grid: 'w-full border-collapse space-y-1',

        weekdays: 'flex',

        weekday:
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',

        week: 'flex w-full mt-2',

        day: 'h-9 w-9 text-center text-sm p-0 relative',

        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal'
        ),

        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-md',

        today: 'bg-accent text-accent-foreground rounded-md',

        outside: 'text-muted-foreground opacity-50',

        disabled: 'text-muted-foreground opacity-50',

        hidden: 'invisible',

        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeft className={cn('h-4 w-4', className)} {...props} />
            );
          }
          return (
            <ChevronRight className={cn('h-4 w-4', className)} {...props} />
          );
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
